/**
 * CircularGauge - Sophisticated circular score indicator
 *
 * Inspired by professional sports analytics dashboards
 * Features:
 * - Animated progress arc
 * - Color-coded thresholds
 * - Configurable size and styling
 * - Inner label and description
 */

import React from 'react';

const SCORE_COLORS = {
  critical: { stroke: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)' },   // Red
  high: { stroke: '#f97316', bg: 'rgba(249, 115, 22, 0.15)' },       // Orange
  medium: { stroke: '#eab308', bg: 'rgba(234, 179, 8, 0.15)' },      // Yellow
  low: { stroke: '#22c55e', bg: 'rgba(34, 197, 94, 0.15)' },         // Green
  none: { stroke: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)' },      // Gray
};

function getScoreColor(value, inverted = false) {
  const thresholds = inverted
    ? { critical: 25, high: 50, medium: 75 }
    : { critical: 75, high: 50, medium: 25 };

  if (inverted) {
    if (value >= thresholds.medium) return SCORE_COLORS.low;
    if (value >= thresholds.high) return SCORE_COLORS.medium;
    if (value >= thresholds.critical) return SCORE_COLORS.high;
    return SCORE_COLORS.critical;
  } else {
    if (value >= thresholds.critical) return SCORE_COLORS.critical;
    if (value >= thresholds.high) return SCORE_COLORS.high;
    if (value >= thresholds.medium) return SCORE_COLORS.medium;
    if (value > 0) return SCORE_COLORS.low;
    return SCORE_COLORS.none;
  }
}

export default function CircularGauge({
  value = 0,
  maxValue = 100,
  label = '',
  sublabel = '',
  size = 'md',
  showPercentage = true,
  inverted = false,
  className = '',
  animated = true,
}) {
  const percentage = Math.min(100, Math.max(0, (value / maxValue) * 100));
  const colors = getScoreColor(percentage, inverted);

  // Size configurations
  const sizes = {
    sm: { dimension: 80, strokeWidth: 6, fontSize: 'text-lg', sublabelSize: 'text-[8px]', labelSize: 'text-[9px]' },
    md: { dimension: 120, strokeWidth: 8, fontSize: 'text-2xl', sublabelSize: 'text-[10px]', labelSize: 'text-xs' },
    lg: { dimension: 160, strokeWidth: 10, fontSize: 'text-4xl', sublabelSize: 'text-xs', labelSize: 'text-sm' },
    xl: { dimension: 200, strokeWidth: 12, fontSize: 'text-5xl', sublabelSize: 'text-sm', labelSize: 'text-base' },
  };

  const config = sizes[size] || sizes.md;
  const radius = (config.dimension - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className={`relative inline-flex flex-col items-center ${className}`}>
      <svg
        width={config.dimension}
        height={config.dimension}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={config.dimension / 2}
          cy={config.dimension / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          fill="none"
          className="text-zinc-800"
        />

        {/* Glow effect */}
        <circle
          cx={config.dimension / 2}
          cy={config.dimension / 2}
          r={radius}
          stroke={colors.stroke}
          strokeWidth={config.strokeWidth + 4}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={`${animated ? 'transition-all duration-1000 ease-out' : ''}`}
          style={{
            filter: `blur(8px)`,
            opacity: 0.3,
          }}
        />

        {/* Progress arc */}
        <circle
          cx={config.dimension / 2}
          cy={config.dimension / 2}
          r={radius}
          stroke={colors.stroke}
          strokeWidth={config.strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={`${animated ? 'transition-all duration-1000 ease-out' : ''}`}
        />
      </svg>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span
          className={`font-bold font-mono ${config.fontSize}`}
          style={{ color: colors.stroke }}
        >
          {showPercentage ? Math.round(value) : value}
        </span>
        {sublabel && (
          <span className={`text-zinc-500 uppercase tracking-wider ${config.sublabelSize}`}>
            {sublabel}
          </span>
        )}
      </div>

      {/* Bottom label */}
      {label && (
        <span className={`mt-2 text-zinc-400 font-medium uppercase tracking-wider ${config.labelSize}`}>
          {label}
        </span>
      )}
    </div>
  );
}

/**
 * CompactGauge - Smaller inline gauge for data tables
 */
export function CompactGauge({ value, maxValue = 100, inverted = false, size = 24 }) {
  const percentage = Math.min(100, Math.max(0, (value / maxValue) * 100));
  const colors = getScoreColor(percentage, inverted);
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-zinc-800"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={colors.stroke}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      <span
        className="absolute text-[9px] font-bold font-mono"
        style={{ color: colors.stroke }}
      >
        {Math.round(value)}
      </span>
    </div>
  );
}

/**
 * ScoreTriad - Three gauges in a row (like Mental Toughness Triad)
 */
export function ScoreTriad({ scores, size = 'sm' }) {
  return (
    <div className="flex items-center justify-around gap-4 p-4 bg-zinc-800/50 rounded-xl">
      {scores.map((score, idx) => (
        <CircularGauge
          key={idx}
          value={score.value}
          label={score.label}
          sublabel={score.sublabel}
          size={size}
          inverted={score.inverted}
        />
      ))}
    </div>
  );
}
